import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

import { useClipTranscriptFetchQueueContext } from '~/providers/ClipTranscriptFetchQueueProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type ClipTranscriptCache, type TimeRange } from '~/swr-hooks/clips/transcript/types';
import { getPrivateClipTranscriptSegmentsKey } from '~/swr-hooks/clips/transcript/useGetPrivateClipTranscriptSegments';
import { fetchClipTranscriptSegments } from '~/swr-hooks/clips/transcript/utils/fetchClipTranscriptSegments';
import { getDataForTimeInterval } from '~/swr-hooks/clips/transcript/utils/getDataForTimeInterval';
import { getRefetchRangesForChangedSegments } from '~/swr-hooks/clips/transcript/utils/getRefetchRangesForChangedSegments';
import { stripRangeFromCachedTranscriptData } from '~/swr-hooks/clips/transcript/utils/stripRangeFromCachedTranscriptData';

export type ChangedTranscriptSegmentRange = Pick<TimeRange, 'startTimeMs' | 'endTimeMs'>;

export type UpdatePrivateClipTranscriptSegmentsParams = {
  clipId: string;
  changedSegments: ChangedTranscriptSegmentRange[];
};

export const useUpdatePrivateClipTranscriptSegments = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const { queueRangeFetch } = useClipTranscriptFetchQueueContext();

  const updatePrivateClipTranscriptSegments = useCallback(
    async ({ clipId, changedSegments }: UpdatePrivateClipTranscriptSegmentsParams) => {
      if (!currentWorkspace || changedSegments.length === 0) {
        return;
      }

      const key = getPrivateClipTranscriptSegmentsKey(clipId);
      const cachedData = queryClient.getQueryData<ClipTranscriptCache>(key)?.data ?? [];
      const refetchRanges = getRefetchRangesForChangedSegments({ changedSegments, cachedData });

      for (const refetchRange of refetchRanges) {
        await queueRangeFetch({
          clipId,
          startTimeMs: refetchRange.startTimeMs,
          endTimeMs: refetchRange.endTimeMs,
          fn: async () => {
            const cache = queryClient.getQueryData<ClipTranscriptCache>(key);
            const hasMore = cache?.hasMore ?? true;
            const videoDurationMs = cache?.videoDurationMs ?? refetchRange.endTimeMs;

            const withoutStale = stripRangeFromCachedTranscriptData({
              data: cache?.data ?? [],
              rangeToStrip: refetchRange,
            });

            try {
              const data = await getDataForTimeInterval({
                startTimeMs: refetchRange.startTimeMs,
                endTimeMs: refetchRange.endTimeMs,
                initialData: withoutStale,
                fetchData: ({ startTimeMs, endTimeMs }) =>
                  fetchClipTranscriptSegments({
                    clipId,
                    workspaceId: currentWorkspace.id,
                    startTimeMs,
                    endTimeMs,
                    videoDurationMs,
                  }),
              });

              const newCache: ClipTranscriptCache = { data, hasMore, videoDurationMs };
              queryClient.setQueryData(key, newCache);
              return newCache;
            } catch (error) {
              reportErrorToBugsnag({
                error: convertUnknownToError(error),
                context: 'Failed to update private clip transcript segments',
                metadata: { clipId, changedSegments },
              });
              return cache ?? { data: [], hasMore: true, videoDurationMs: refetchRange.endTimeMs };
            }
          },
        });
      }
    },
    [currentWorkspace, queryClient, queueRangeFetch],
  );

  return {
    updatePrivateClipTranscriptSegments,
  };
};
